import React, { useContext } from 'react'
import { groupBy } from 'lodash'
import {
  TeamWishlistItem,
  TeamWishlistItemCharacterWish,
  TeamWishlistItemCharacterWishlist,
} from 'app/frontend/types/custom/TeamWishlistEncounter'
import WishLegacyPriority, {
  wishLegacyPriorities,
} from 'app/frontend/types/custom/WishLegacyPriority'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { LootContext } from '../context'
import { useWishLabels } from '../helpers'
import { difficulties } from 'app/frontend/types/custom/Difficulty'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import { Link } from 'react-router-dom'
import { ArrowUp, Info, MessageCircleMore } from 'lucide-react'
import { upgradeTracks } from 'app/frontend/types/custom/UpgradeTrack'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { useTeam } from 'app/frontend/base/config/context/team'
import ItemSlot from 'app/frontend/types/custom/ItemSlot'
import TeamWishTooltip from './TeamWishTooltip'
import Card from 'app/frontend/base/components/Card'
import WishLegacyPriorityDot from 'app/frontend/base/components/WishLegacyPriorityDot'

type Props = {
  wishlist?: TeamWishlistItemCharacterWishlist
  characterInfo: wowaudit.CharacterReference
  currentTrack: number | null
  equippedItemLevels?: Record<ItemSlot, number | null>
  item: TeamWishlistItem
}

const TeamWish = ({
  wishlist,
  characterInfo,
  currentTrack,
  equippedItemLevels,
  item,
}: Props) => {
  const { team } = useTeam()
  const { valueDisplay, difficulty } = useContext(LootContext)
  const wishLabels = useWishLabels()
  const difficultyIndex = difficulties.indexOf(difficulty)

  const wishUpgrade = (
    wishes: TeamWishlistItemCharacterWish[],
    specId: number,
  ) => {
    const customLevelWishes = wishes.filter((wish) =>
      wishLegacyPriorities.includes(wish.wish_type as WishLegacyPriority),
    )

    if (
      customLevelWishes.length > 0 ||
      typeof wishlist?.score_by_spec[specId.toString()]?.percentage === 'string'
    ) {
      return customLevelWishes.map((wish, i) => (
        <span key={i}>
          <Tooltip>
            <TooltipTrigger>
              <WishLegacyPriorityDot
                priority={(wish.upgrade_level as WishLegacyPriority) ?? 'none'}
              />
            </TooltipTrigger>
            <TooltipContent>
              {wishLabels[wish.wish_type as WishLegacyPriority]}
            </TooltipContent>
          </Tooltip>
        </span>
      ))
    } else if (
      valueDisplay === 'percentage' ||
      wishes.filter((wish) => wish.wish_type === 'percentage').length > 0
    ) {
      return `${wishlist?.score_by_spec[
        specId.toString()
      ]?.percentage?.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      })}%`
    } else {
      return (
        wishlist?.score_by_spec[specId.toString()]?.score as number
      )?.toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })
    }
  }

  return (
    <Card
      className={`rounded-md p-1 border border-success-foreground text-success-foreground flex gap-1 justify-between ${
        wishlist?.edited
          ? ' border-warning-foreground text-warning-foreground'
          : ''
      }${
        wishlist?.outdated
          ? ' border-destructive-foreground text-destructive-foreground'
          : ''
      }${!wishlist ? ' border-border' : ''}`}
    >
      <div className='flex gap-1.5 min-w-0 flex-1'>
        <CharacterClassIcon
          characterClass={characterInfo.characterClass}
          size='small'
          className='border-2 h-5 w-5 shrink-0'
        />
        <Link
          to={characterInfo.defaultPath}
          className='text-foreground truncate min-w-0'
        >
          {characterInfo.name}
        </Link>
      </div>

      <div className='flex gap-1.5'>
        {wishlist?.comment && (
          <div className='text-foreground'>
            <Tooltip>
              <TooltipTrigger>
                <MessageCircleMore size={14} />
              </TooltipTrigger>
              <TooltipContent>{wishlist.comment}</TooltipContent>
            </Tooltip>
          </div>
        )}

        {currentTrack && currentTrack - difficultyIndex > 0 ? (
          <div className='inline-flex items-start mt-0.5'>
            <Tooltip>
              <TooltipTrigger className='rounded-md h-4 px-1.5 bg-muted border inline-flex gap-0.5 items-center text-xs'>
                <ArrowUp size={14} className='text-success-foreground' />
                <span className='text-foreground'>
                  {currentTrack === 6 ? '?' : currentTrack - difficultyIndex}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {`Currently equipped item is ${
                  currentTrack === 6 && item.slot
                    ? `not on an upgrade track (item level ${
                        equippedItemLevels?.[item.slot] || 'unknown'
                      })`
                    : ` on the ${upgradeTracks[currentTrack]} track`
                }`}
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          ''
        )}

        {!wishlist ? (
          ''
        ) : (
          <React.Fragment>
            <div className='flex flex-col w-fit'>
              {Object.values(
                groupBy(wishlist.wishes, (wish) => wish.specialization_id),
              ).map((wishes, i) => (
                <span key={i} className='flex items-center gap-1.5'>
                  {wishes[0]?.catalyst ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <BlizzardIcon
                          size='small'
                          className='rounded-none min-w-[12px] min-h-[12px]'
                          iconName='inv_progenitor_protoformsynthesis'
                        />
                      </TooltipTrigger>
                      <TooltipContent>Catalyst</TooltipContent>
                    </Tooltip>
                  ) : (
                    ''
                  )}
                  <BlizzardIcon
                    iconName={wishes[0].specialization_icon}
                    size='small'
                  />
                  <Link
                    to={`${team.path}/loot/characters/${characterInfo.id}`}
                    className='shrink-0'
                  >
                    {wishUpgrade(wishes, wishes[0].specialization_id)}
                  </Link>
                </span>
              ))}
            </div>

            <div>
              <TeamWishTooltip wishlist={wishlist}>
                <Info
                  size={14}
                  className={`mt-[0.2rem] cursor-pointer ${
                    wishlist.alternative
                      ? 'text-warning-foreground'
                      : 'text-foreground'
                  }`}
                />
              </TeamWishTooltip>
            </div>
          </React.Fragment>
        )}
      </div>
    </Card>
  )
}

export default TeamWish
